import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, Loader, Lu } from "@/components/shared";
import { Button } from "@/components/ui/button";

const ICON = { leak: "droplets", battery: "battery-low", offline: "wifi-off", door: "door-open", hvac: "thermometer", energy: "zap" };

export default function Alerts() {
  const { t, lang } = useLang();
  const { selectedId } = useProperty();
  const [alerts, setAlerts] = useState(null);

  const load = () => client.get(`/properties/${selectedId}/alerts`).then((r) => setAlerts(r.data));
  useEffect(() => { if (selectedId) { setAlerts(null); load(); } }, [selectedId]);

  const resolve = async (id) => {
    await client.post(`/properties/${selectedId}/alerts/${id}/resolve`);
    setAlerts((a) => a.map((x) => (x.id === id ? { ...x, resolved: true } : x)));
    toast.success(t("resolved"));
  };

  if (!alerts) return <Loader />;
  const active = alerts.filter((a) => !a.resolved);
  const done = alerts.filter((a) => a.resolved);

  const sev = { high: "#F43F5E", medium: "#F59E0B", low: "#64748B" };

  const Row = (a) => (
    <motion.div key={a.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: 30 }}
      data-testid={`alert-${a.id}`}
      className={`md-card p-4 flex items-center gap-4 ${a.resolved ? "opacity-50" : ""}`}
      style={{ borderColor: a.resolved ? undefined : `${sev[a.severity]}44` }}>
      <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${sev[a.severity]}1a` }}>
        <Lu name={ICON[a.type] || "triangle-alert"} className="w-5 h-5" style={{ color: sev[a.severity] }} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{a[lang === "ar" ? "message_ar" : "message_en"]}</p>
        <p className="text-xs text-muted-foreground/70 mt-0.5">{t("room")} {a.room_number} · {new Date(a.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</p>
      </div>
      <span className="text-xs font-medium px-2.5 py-1 rounded-full shrink-0" style={{ color: sev[a.severity], background: `${sev[a.severity]}1a` }}>{t(a.severity)}</span>
      {!a.resolved && (
        <Button size="sm" data-testid={`resolve-${a.id}`} onClick={() => resolve(a.id)}
          className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/30 h-8">{t("resolve")}</Button>
      )}
    </motion.div>
  );

  return (
    <div>
      <SectionHeader icon="bell-ring" title={t("alerts")} subtitle={`${active.length} ${t("openAlerts")}`} />
      <div className="space-y-3">
        <AnimatePresence>{active.map(Row)}</AnimatePresence>
        {active.length === 0 && <div className="md-card p-10 text-center text-muted-foreground/70"><Lu name="check-check" className="w-10 h-10 mx-auto text-emerald-400 mb-2" />{t("resolved")}</div>}
        {done.length > 0 && <p className="text-xs text-muted-foreground/70 pt-4">{t("resolved")} ({done.length})</p>}
        {done.map(Row)}
      </div>
    </div>
  );
}
